"use client";

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@universe/ui/components/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@universe/ui/components/tooltip";
import { cn } from "@universe/ui/lib/utils";
import { LifeBuoy } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { Route } from "@/enums/route";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { isRouteActive } from "@/utils/routeSegments";

export function NavSupport() {
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();
  const tNav = useTranslations("nav");
  const pathname = usePathname();
  const { isSupportEnabled } = useFeatureFlags();

  const isActive = isRouteActive(pathname, Route.Support);

  return (
    <SidebarMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarMenuItem>
            <SidebarMenuButton
              className={cn(
                "group/brand-hover relative h-11 hover:bg-transparent active:bg-transparent",
                isActive &&
                  "bg-white/25 scale-101 transition-all duration-200 ease-in-out shadow-lg"
              )}
              asChild
              disabled={!isSupportEnabled}
              tooltip={tNav("support")}
              onClick={() => {
                if (!isSupportEnabled) return;
                router.push(Route.Support);
                if (isMobile) {
                  setOpenMobile(false);
                }
              }}
            >
              <a
                href="#"
                data-testid="nav-support"
                className={cn(
                  "pl-4",
                  !isSupportEnabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="brand-hover-overlay rounded-md" />
                <LifeBuoy
                  strokeWidth={isActive ? 2 : 1.5}
                  className="!h-5 !w-5 transition-all duration-200 ease-in-out"
                  style={{
                    stroke: "var(--menu-font-color)",
                    filter:
                      "drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)) drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 60%, transparent))",
                  }}
                />
                <span
                  className={cn(
                    "text-lg transition-all duration-200 ease-in-out",
                    isActive && "font-semibold text-lg",
                    !isActive &&
                      "font-medium tracking-wide opacity-90 hover:opacity-100"
                  )}
                  style={{
                    color: "var(--menu-font-color)",
                    textShadow:
                      "-0.5px 0.5px 1px color-mix(in oklch, var(--menu-font-shadow) 20%, transparent), 0 0.5px 2px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent), 1px 1px 0px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)",
                  }}
                >
                  {tNav("support")}
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </TooltipTrigger>
        {!isSupportEnabled && (
          <TooltipContent
            side="right"
            align="center"
            className="text-white rounded-lg shadow-lg text-sm font-semibold bg-primary"
          >
            <p>{tNav("comingSoon")}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </SidebarMenu>
  );
}
